<script setup lang="ts">
import { THEME } from '@pkg/config/misc/theme';
import type { PrivacyBlockControlLabels } from '@pkg/types/privacyControls';
import { Switch } from '@pkg/ui/components/ui/switch';
import {
  COOKIE_CONSENT_CATEGORIES,
  toCookieInformationKey,
  useCookieConsent,
  type CookieConsentCategory,
} from '@pkg/ui/composables/useCookieConsent';
import PortableEditor from '@pkg/portable-text-editor/web/PortableEditor.vue';
import type { PortableTextTextBlock } from '@sanity/types';
import { cn } from '@pkg/ui/lib/utils';
const props =
  defineProps<{
    preTitle?: string;
    title: string;
    description?: Array<PortableTextTextBlock>;
    controlLabels: PrivacyBlockControlLabels;
    contentAlignment?: 'left' | 'center';
    theme?: THEME;
    anchor?: string;
  }>();

const consentRefs = useCookieConsent([...COOKIE_CONSENT_CATEGORIES]);

/** Plain booleans for template `consent[key]` access (nested refs do not auto-unwrap). */
const consent = computed(() => ({
  marketing: consentRefs.marketing.value,
  statistic: consentRefs.statistic.value,
  functional: consentRefs.functional.value,
}));

const toggleConsent = (key: CookieConsentCategory, nextChecked: boolean) => {
  const current = consentRefs[key].value;
  if (nextChecked === current) {
    return;
  }

  if (window?.CookieInformation) {
    window.CookieInformation.changeCategoryConsentDecision(toCookieInformationKey(key));
    window.CookieInformation.submitConsent();
  }

  consentRefs[key].value = nextChecked;
};
</script>

<template>
  <PartialSection :theme="props.theme ?? THEME.DARK" :anchor="props.anchor">
    <div class="c-privacy-control-section__wrapper wrapper mt-40">
      <div
        :class="cn('m-blocks m-blocks--inner', {
          'text-center': props.contentAlignment === 'center',
        })"
      >
        <BaseTitle
          :title="title"
          :pre-title="preTitle"
          class="mb-24"
          tag="h1"
          :variant="props.contentAlignment === 'center' ? 'center' : 'default'"
        />
        <div v-if="description?.length" class="mb-24">
          <PortableEditor :content="description" />
        </div>
        <div
          v-for="key in COOKIE_CONSENT_CATEGORIES"
          v-show="controlLabels[key]"
          :key="key"
          :class="cn('c-privacy-control-section__control-row', {
            'justify-center': props.contentAlignment === 'center',
          })"
        >
          <Switch
            :aria-label="key"
            :model-value="!!consent[key]"
            size="large"
            @update:model-value="toggleConsent(key, $event)"
          />
          <span
            v-if="controlLabels[key]"
            class="c-privacy-control-section__control-label"
          >
            {{ controlLabels[key] }}
          </span>
        </div>
      </div>
    </div>
  </PartialSection>
</template>
<style scoped>
.c-privacy-control-section {
  &__control-row {
    display: flex;
    padding-bottom: theme('spacing.16');
  }

  &__control-label {
    gap: theme('spacing.4');
    padding-left: theme('spacing.16');
  }
}
</style>
